import { createGlobalStyle } from 'styled-components';
import fonts from './fonts';
import variables from './variables';
import TransitionStyles from './TransitionStyles';
import PrismStyles from './PrismStyles';

const GlobalStyle = createGlobalStyle`
  ${fonts};
  ${variables};

  html {
    box-sizing: border-box;
    width: 100%;
    scroll-behavior: smooth;
  }

  *,
  *:before,
  *:after {
    box-sizing: inherit;
  }

  ::selection {
    background-color: var(--text-primary);
    color: var(--bg-primary);
  }

  /* Provide basic, default focus styles.*/
  :focus {
    outline: 1px solid var(--text-primary);
    outline-offset: 2px;
  }

  /*
    Remove default focus styles for mouse users ONLY if
    :focus-visible is supported on this platform.
  */
  :focus:not(:focus-visible) {
    outline: none;
    outline-offset: 0px;
  }

  /*
    Optionally: If :focus-visible is supported on this
    platform, provide enhanced focus styles for keyboard
    focus.
  */
  :focus-visible {
    outline: 1px solid var(--text-primary);
    outline-offset: 2px;
  }

  /* Scrollbar Styles - Minimal Japanese */
  html {
    scrollbar-width: thin;
    scrollbar-color: var(--text-primary) var(--bg-primary);
  }
  ::-webkit-scrollbar {
    width: 4px;
  }
  ::-webkit-scrollbar-track {
    background: var(--bg-primary);
  }
  ::-webkit-scrollbar-thumb {
    background-color: var(--text-primary);
    border-radius: 0;
    opacity: 0.3;
  }
  ::-webkit-scrollbar-thumb:hover {
    opacity: 0.6;
  }

  body {
    margin: 0;
    width: 100%;
    min-height: 100%;
    overflow-x: hidden;
    -moz-osx-font-smoothing: grayscale;
    -webkit-font-smoothing: antialiased;
    background-color: var(--bg-primary);
    background-image: 
      repeating-linear-gradient(
        0deg,
        transparent,
        transparent 2px,
        rgba(0, 0, 0, 0.01) 2px,
        rgba(0, 0, 0, 0.01) 4px
      );
    background-attachment: fixed;
    color: var(--text-primary);
    font-family: var(--font-body);
    font-size: var(--fz-lg);
    line-height: 1.8;
    font-weight: 400;

    @media (max-width: 480px) {
      font-size: var(--fz-lg);
    }

    &.hidden {
      overflow: hidden;
    }

    &.blur {
      overflow: hidden;

      header {
        background-color: transparent;
      }

      #content > * {
        filter: blur(5px) brightness(0.7);
        transition: var(--transition);
        pointer-events: none;
        user-select: none;
      }
    }
  }

  #root {
    min-height: 100vh;
    display: grid;
    grid-template-rows: 1fr auto;
    grid-template-columns: 100%;
  }

  main {
    margin: 0;
    width: 100%;
    min-height: 100vh;

    &.fillHeight {
      padding: 0;
    }
  }

  section {
    margin: 0;
    padding: 0;
    position: relative;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0;
    font-weight: 600;
    color: var(--text-primary);
    line-height: 1.15;
    letter-spacing: -0.03em;
    font-family: var(--font-display);
  }

  h1 {
    font-size: var(--fz-hero);
    font-weight: 700;
    line-height: 0.9;
    letter-spacing: -0.04em;
  }

  h2 {
    font-size: var(--fz-hero-sub);
    font-weight: 600;
    line-height: 1.1;
    letter-spacing: -0.03em;
  }

  h3 {
    font-size: var(--fz-heading-lg);
    font-weight: 600;
    line-height: 1.2;
  }

  .big-heading {
    margin: 0;
    font-size: var(--fz-hero);
    font-weight: 700;
    line-height: 0.9;
    letter-spacing: -0.04em;
  }

  .medium-heading {
    margin: 0;
    font-size: var(--fz-hero-sub);
    font-weight: 600;
    line-height: 1.1;
    letter-spacing: -0.03em;
  }

  .numbered-heading {
    display: flex;
    align-items: center;
    position: relative;
    margin: 0 0 var(--space-3xl) 0;
    width: 100%;
    font-size: var(--fz-heading);
    white-space: nowrap;
    font-weight: 600;
    font-family: var(--font-display);
    letter-spacing: -0.02em;

    &:before {
      position: relative;
      bottom: 2px;
      counter-increment: section;
      content: '0' counter(section) '.';
      margin-right: var(--space-md);
      color: var(--text-tertiary);
      font-family: var(--font-mono);
      font-size: var(--fz-lg);
      font-weight: 400;
      letter-spacing: 0;
      opacity: 0.6;

      @media (max-width: 480px) {
        margin-bottom: -3px;
        margin-right: 5px;
      }
    }

    &:after {
      content: '';
      display: block;
      position: relative;
      top: -3px;
      width: 200px;
      height: 1px;
      margin-left: var(--space-lg);
      background: linear-gradient(to right, var(--text-primary), transparent);
      opacity: 0.3;

      @media (max-width: 1080px) {
        width: 200px;
      }
      @media (max-width: 768px) {
        width: 100%;
      }
      @media (max-width: 600px) {
        margin-left: 10px;
      }
    }
  }

  img,
  svg,
  .gatsby-image-wrapper {
    width: 100%;
    max-width: 100%;
    vertical-align: middle;
  }

  img[alt=""],
  img:not([alt]) {
    filter: blur(5px);
  }

  svg {
    width: 100%;
    height: 100%;
    fill: currentColor;
    vertical-align: middle;

    &.feather {
      fill: none;
    }
  }

  a {
    display: inline-block;
    text-decoration: none;
    text-decoration-skip-ink: auto;
    color: inherit;
    position: relative;
    transition: var(--transition);

    &:hover,
    &:focus {
      color: var(--text-primary);
      opacity: 0.7;
    }

    &.inline-link {
      ${({ theme }) => theme.mixins.inlineLink};
    }
  }

  button {
    cursor: pointer;
    border: 0;
    border-radius: 0;
  }

  input, textarea {
    border-radius: 0;
    outline: 0;

    &:focus {
      outline: 0;
    }
    &:focus,
    &:active {
      &::placeholder {
        opacity: 0.5;
      }
    }
  }

  p {
    margin: 0 0 15px 0;

    &:last-child,
    &:last-of-type {
      margin: 0;
    }

    & > a {
      ${({ theme }) => theme.mixins.inlineLink};
    }

    & > code {
      background-color: var(--light-navy);
      color: var(--white);
      font-size: var(--fz-sm);
      border-radius: var(--border-radius);
      padding: 0.3em 0.5em;
    }
  }

  ul {
    &.fancy-list {
      padding: 0;
      margin: 0;
      list-style: none;
      font-size: var(--fz-lg);
      li {
        position: relative;
        padding-left: var(--space-xl);
        margin-bottom: var(--space-md);
        color: var(--text-secondary);
        &:before {
          content: '—';
          position: absolute;
          left: 0;
          color: var(--text-primary);
        opacity: 0.7;
          font-size: 1em;
          font-weight: 300;
        }
      }
    }
  }

  blockquote {
    border-left-color: var(--text-primary);
    border-left-style: solid;
    border-left-width: 2px;
    margin-left: 0px;
    margin-right: 0px;
    padding-left: var(--space-xl);
    background: rgba(0, 0, 0, 0.02);
    padding: var(--space-lg) var(--space-xl);

    p {
      font-style: italic;
      font-size: 24px;
    }
  }

  hr {
    background-color: var(--lightest-navy);
    height: 1px;
    border-width: 0px;
    border-style: initial;
    border-color: initial;
    border-image: initial;
    margin: 1rem;
  }

  code {
    font-family: var(--font-mono);
    font-size: var(--fz-md);
  }

  .skip-to-content {
    ${({ theme }) => theme.mixins.button};
    position: absolute;
    top: auto;
    left: -999px;
    width: 1px;
    height: 1px;
    overflow: hidden;
    z-index: -99;

    &:hover,
    &:focus {
      background-color: var(--text-primary);
      color: var(--bg-primary);
      top: 0;
      left: 0;
      width: auto;
      height: auto;
      overflow: auto;
      z-index: 99;
      box-shadow: none;
      transform: none;
    }
  }

  #logo {
    color: var(--text-primary);
  }

  .overline {
    color: var(--text-tertiary);
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
    font-weight: 400;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    opacity: 0.7;
  }

  .subtitle {
    color: var(--text-tertiary);
    margin: 0 0 var(--space-lg) 0;
    font-size: var(--fz-md);
    font-family: var(--font-mono);
    font-weight: 400;
    line-height: 1.6;
    opacity: 0.8;
    @media (max-width: 1080px) {
      font-size: var(--fz-sm);
    }
    @media (max-width: 768px) {
      font-size: var(--fz-xs);
    }

    a {
      ${({ theme }) => theme.mixins.inlineLink};
      line-height: 1.5;
    }
  }

  .breadcrumb {
    display: flex;
    align-items: center;
    margin-bottom: 50px;
    color: var(--green);

    .arrow {
      display: block;
      margin-right: 10px;
      padding-top: 4px;
      color: var(--japanese-red);
    }

    a {
      ${({ theme }) => theme.mixins.inlineLink};
      font-family: var(--font-mono);
      font-size: var(--fz-sm);
      font-weight: 500;
      line-height: 1.5;
      text-transform: uppercase;
      letter-spacing: 0.15em;
      color: var(--text-primary);
      opacity: 0.7;
    }
  }

  .gatsby-image-outer-wrapper {
    height: 100%;
  }

  ${TransitionStyles};

  ${PrismStyles};
`;

export default GlobalStyle;
